import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  storefrontLanguages,
  translateMessage,
  type StorefrontLanguage,
  type TranslationValues,
} from '@/i18n';
import { listShopifyProductTranslations } from '@workspace/api-client-react';
import { setRemoteProductTranslations } from '@/i18n/product-translations';
import { scheduleAfterFirstPaint } from '@/lib/schedule-after-first-paint';

const LANGUAGE_STORAGE_KEY = 'al-malaki-language-v1';

interface LanguageContextValue {
  language: StorefrontLanguage;
  shopifyLanguageCode: 'EN';
  direction: 'ltr' | 'rtl';
  setLanguage: (language: StorefrontLanguage) => void;
  t: (message: string, values?: TranslationValues) => string;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

function readSavedLanguage(): StorefrontLanguage {
  if (typeof window === 'undefined') return 'en';
  try {
    const saved = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return storefrontLanguages.find((option) => option.code === saved)?.code ?? 'en';
  } catch {
    return 'en';
  }
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<StorefrontLanguage>(readSavedLanguage);
  const [productTranslationRevision, setProductTranslationRevision] = useState(0);
  const direction: 'ltr' | 'rtl' = language === 'ar' ? 'rtl' : 'ltr';
  // Shopify remains the English source catalog; app dictionaries localize the rendered copy.
  const shopifyLanguageCode: 'EN' = 'EN';

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = direction;
    try {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    } catch {
      // The selected language still applies for this session without storage.
    }
  }, [direction, language]);

  useEffect(() => {
    let cancelled = false;

    const loadProductTranslations = async () => {
      try {
        const response = await listShopifyProductTranslations();
        if (cancelled) return;
        setRemoteProductTranslations(response.translations);
        setProductTranslationRevision((revision) => revision + 1);
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('Unable to load app-managed product translations.', error);
        }
      }
    };

    const cancelScheduledLoad = scheduleAfterFirstPaint(() => {
      void loadProductTranslations();
    });
    const interval = window.setInterval(
      () => void loadProductTranslations(),
      60_000,
    );

    return () => {
      cancelled = true;
      cancelScheduledLoad();
      window.clearInterval(interval);
    };
  }, []);

  const setLanguage = useCallback((nextLanguage: StorefrontLanguage) => {
    setLanguageState(nextLanguage);
  }, []);

  const t = useCallback(
    (message: string, values?: TranslationValues) =>
      translateMessage(language, message, values),
    [language],
  );

  const value = useMemo(
    () => ({ language, shopifyLanguageCode, direction, setLanguage, t }),
    [
      direction,
      language,
      productTranslationRevision,
      setLanguage,
      shopifyLanguageCode,
      t,
    ],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
}

export type { StorefrontLanguage };